import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  LogOut,
  Gem,
  ShoppingBag,
  Footprints,
  Shirt,
  Scissors,
  Loader2,
  Settings2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useOutfitStore } from '@/stores/outfitStore';
import type { OutfitCategory } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { OutfitCard } from '@/components/outfit/OutfitCard';
import { TemplateSelector } from '@/components/outfit/TemplateSelector';
import { PDFExport } from '@/components/outfit/PDFExport';
import { LanguageSelector } from '@/components/outfit/LanguageSelector';
import { AutoSaveIndicator } from '@/components/outfit/AutoSaveIndicator';
import { PlannerFAB } from '@/components/outfit/PlannerFAB';
import { StyleExportSheet } from '@/components/outfit/StyleExportSheet';
import { SponsorBadge } from '@/components/SponsorBadge';

const categoryConfig = {
  hair: { icon: Scissors, title: 'outfit.hair', maxImages: 2 },
  jewelry: { icon: Gem, title: 'outfit.jewelry', maxImages: 4 },
  purse: { icon: ShoppingBag, title: 'outfit.purse', maxImages: 2 },
  shoes: { icon: Footprints, title: 'outfit.shoes', maxImages: 2 },
  outfit: { icon: Shirt, title: 'outfit.title', maxImages: 4 },
};

export const OutfitPlannerPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const { logout } = useAuthStore();
  const {
    currentEvent,
    currentDay,
    currentOutfit,
    outfits,
    selectedTemplate,
    isLoading,
    events,
    fetchOutfits,
    setCurrentEvent,
    setCurrentDay,
    updateEventDays,
  } = useOutfitStore();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [isEditDaysOpen, setIsEditDaysOpen] = useState(false);
  const [editDaysCount, setEditDaysCount] = useState(12);
  const [isUpdatingDays, setIsUpdatingDays] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    const id = parseInt(eventId);
    const existing = events.find(e => e.id === id);
    if (existing) {
      setCurrentEvent(existing); // internally calls fetchOutfits
    } else {
      fetchOutfits(id); // fetchOutfits also sets currentEvent from API response
    }
  }, [eventId]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const visibleCategories: OutfitCategory[] = selectedTemplate
    ? selectedTemplate.categories
    : ['outfit', 'jewelry', 'shoes', 'purse', 'hair'];

  const totalDays = currentEvent?.total_days || 12;

  const getDayHasContent = (day: number) => {
    const o = outfits.find(x => x.day_number === day);
    if (!o) return false;
    return (
      (o.hair_images?.length || 0) +
      (o.jewelry_images?.length || 0) +
      (o.purse_images?.length || 0) +
      (o.shoes_images?.length || 0) +
      (o.outfit_images?.length || 0)
    ) > 0;
  };

  return (
    <div className="min-h-dvh bg-background">
      {/* ── Sticky header ── */}
      <header className="bg-white/90 backdrop-blur-md border-b border-border sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          {/* Left */}
          <div className="flex items-center gap-2 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/events')}
              className="rounded-full w-9 h-9 text-muted-foreground hover:text-foreground flex-shrink-0"
              aria-label={t('common.backToEvents')}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-serif italic font-bold text-base sm:text-lg text-foreground truncate leading-tight">
                {currentEvent?.name || '…'}
              </h1>
              <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider hidden sm:block">
                {totalDays} {t('outfit.day').toLowerCase()}s
              </p>
            </div>
          </div>

          {/* Right */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <AutoSaveIndicator />
            <div className="hidden lg:block">
              <SponsorBadge variant="inline" />
            </div>

            {/* Edit days dialog */}
            <Dialog open={isEditDaysOpen} onOpenChange={setIsEditDaysOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full w-9 h-9 text-muted-foreground hover:text-foreground"
                  onClick={() => setEditDaysCount(currentEvent?.total_days || 12)}
                  aria-label={t('common.editDays')}
                >
                  <Settings2 className="w-4 h-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-3xl border-border shadow-2xl shadow-primary/10 max-w-sm w-[92vw]">
                <DialogHeader>
                  <DialogTitle className="font-serif italic text-xl text-foreground">
                    {t('edit.daysTitle', 'Modifica Giorni')}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <p className="text-sm text-muted-foreground font-medium leading-relaxed">
                    {t(
                      'edit.daysDescription',
                      'Rimuovere giorni eliminerà permanentemente i look pianificati per quei giorni.',
                    )}
                  </p>
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-foreground ml-1">
                      {t('event.totalDays')}
                    </label>
                    <Input
                      type="number"
                      min={1}
                      max={30}
                      value={editDaysCount}
                      onChange={e => setEditDaysCount(parseInt(e.target.value) || 1)}
                      className="h-12 rounded-xl border-border focus-visible:ring-primary/30 focus-visible:border-primary"
                    />
                  </div>
                  <Button
                    onClick={async () => {
                      if (!currentEvent) return;
                      setIsUpdatingDays(true);
                      await updateEventDays(currentEvent.id, editDaysCount);
                      setIsUpdatingDays(false);
                      setIsEditDaysOpen(false);
                    }}
                    disabled={isUpdatingDays}
                    className="w-full h-12 rounded-xl bg-gradient-to-r from-[#E91E8C] to-primary hover:opacity-90 text-white font-bold shadow-md shadow-primary/20 active:scale-95 transition-all"
                  >
                    {isUpdatingDays ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      t('common.save', 'Salva')
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <LanguageSelector />
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="rounded-full w-9 h-9 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* ── Mobile: day strip ── */}
      <div className="lg:hidden bg-white border-b border-border px-4 py-2">
        <div className="flex items-center gap-3">
          <span className="font-serif italic font-bold text-lg text-foreground whitespace-nowrap">
            {t('outfit.day')} {currentDay}
            <span className="text-xs text-muted-foreground font-sans font-semibold ml-1 not-italic">
              / {totalDays}
            </span>
          </span>
          {/* Scrollable dots */}
          <div className="flex-1 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            <div className="flex items-center gap-1 py-1">
              {Array.from({ length: Math.min(totalDays, 30) }, (_, i) => {
                const day = i + 1;
                const isActive = day === currentDay;
                const hasContent = getDayHasContent(day);
                return (
                  <button
                    key={day}
                    onClick={() => setCurrentDay(day)}
                    aria-label={`${t('outfit.day')} ${day}`}
                    aria-pressed={isActive}
                    className={`h-2 flex-shrink-0 rounded-full transition-all duration-300 ${
                      isActive
                        ? 'w-6 bg-primary'
                        : hasContent
                        ? 'w-2 bg-accent'
                        : 'w-2 bg-border'
                    }`}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Main ── */}
      <main className="max-w-7xl mx-auto">
        {isLoading && !currentOutfit ? (
          <div className="flex justify-center py-24" aria-busy="true" aria-label="Caricamento">
            <Loader2 className="w-7 h-7 text-primary animate-spin" />
          </div>
        ) : (
          <div className="flex">
            {/* ── Desktop sidebar ── */}
            <aside className="hidden lg:flex flex-col gap-4 w-72 xl:w-80 shrink-0 sticky top-[61px] self-start h-[calc(100dvh-61px)] overflow-y-auto p-5 border-r border-border">
              {/* Day grid */}
              <div className="bg-white rounded-2xl border border-border p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
                  {t('navigation.days', 'Giorni')}
                </p>
                <div className="grid grid-cols-4 gap-1.5">
                  {Array.from({ length: totalDays }, (_, i) => {
                    const day = i + 1;
                    const isActive = day === currentDay;
                    const hasContent = getDayHasContent(day);
                    return (
                      <button
                        key={day}
                        onClick={() => setCurrentDay(day)}
                        aria-label={`${t('outfit.day')} ${day}`}
                        aria-pressed={isActive}
                        className={`h-9 rounded-xl text-sm font-bold transition-all ${
                          isActive
                            ? 'bg-primary text-white shadow-md shadow-primary/20'
                            : hasContent
                            ? 'bg-secondary text-primary hover:bg-secondary/80'
                            : 'bg-background text-muted-foreground border border-border hover:border-primary/40 hover:text-primary'
                        }`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
                {/* Prev / next */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                  <button
                    onClick={() => currentDay > 1 && setCurrentDay(currentDay - 1)}
                    disabled={currentDay === 1}
                    className="flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-primary disabled:opacity-40 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    {t('navigation.back')}
                  </button>
                  <span className="font-serif italic text-sm font-bold text-foreground">
                    {currentDay}/{totalDays}
                  </span>
                  <button
                    onClick={() => currentDay < totalDays && setCurrentDay(currentDay + 1)}
                    disabled={currentDay === totalDays}
                    className="flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-primary disabled:opacity-40 transition-colors"
                  >
                    {t('navigation.next')}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <TemplateSelector />
              <PDFExport />
              <SponsorBadge variant="card" />
            </aside>

            {/* ── Cards area ── */}
            <div className="flex-1 min-w-0 px-4 lg:px-6 py-5 pb-28 lg:pb-8">
              {/* Desktop day heading */}
              <div className="hidden lg:flex items-baseline gap-2 mb-5">
                <h2 className="font-serif italic text-2xl font-bold text-foreground">
                  {t('outfit.day')} {currentDay}
                </h2>
                <span className="text-sm text-muted-foreground font-semibold">/ {totalDays}</span>
              </div>

              {currentOutfit ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {visibleCategories.map((categoryKey, index) => {
                    const config = categoryConfig[categoryKey as keyof typeof categoryConfig];
                    const Icon = config.icon;
                    const images =
                      (currentOutfit[
                        `${categoryKey}_images` as keyof typeof currentOutfit
                      ] as string[]) || [];
                    const notes =
                      (currentOutfit[categoryKey as keyof typeof currentOutfit] as string) || '';

                    return (
                      <div
                        key={`${categoryKey}-${currentDay}`}
                        className="animate-spring-in"
                        style={{ animationDelay: `${index * 60}ms`, animationFillMode: 'both' }}
                      >
                        <OutfitCard
                          title={t(config.title)}
                          category={categoryKey}
                          icon={<Icon className="w-5 h-5" />}
                          outfitId={currentOutfit.id}
                          images={images}
                          notes={notes}
                          maxImages={config.maxImages}
                          isOpen={!!selectedTemplate?.categories.includes(categoryKey)}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex justify-center py-16">
                  <Loader2 className="w-6 h-6 text-primary animate-spin" />
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Mobile FAB */}
      {currentEvent && (
        <PlannerFAB totalDays={totalDays} onOpenSheet={() => setSheetOpen(true)} />
      )}

      {/* Style & Export bottom sheet (mobile) */}
      <StyleExportSheet isOpen={sheetOpen} onClose={() => setSheetOpen(false)} />
    </div>
  );
};
